import { useState, useEffect, useMemo } from 'react';
import {
  buscarTelaAtual,
  getCodDepartamentoSession,
  getConfigAmbiente,
  getMenusDepartamento,
  getNomeDepartamentoSession
} from '../../../services/authentication/auth';
import { deletarTelaFavoritada, criarFavorito } from '../../../api/autenticacao';
import { useNavegacao } from '../../../hooks/useNavegacao';
import { ProjetosEnum } from '../../Shared/Enums/ProjetosEnum';
import { useMenuSidebar } from '../../../contexts/MenuSidebarContext';
import { montarItensEsteira } from './esteiraSidebar.utils';

const normalizarTexto = texto =>
  (texto || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

const useListItemsHook = ({
  departamentoSelecionado,
  handleDrawerToggle,
  setEscopoAcesso,
  setScrIframe
}) => {
  const config = getConfigAmbiente();
  const corBaseCliente =
    config && config.cores && config.cores.CORBASECLIENTE ? config.cores.CORBASECLIENTE : '#666666';
  document.documentElement.style.setProperty('--corBaseCliente', corBaseCliente);
  document.documentElement.style.setProperty('--corBaseClienteHover', corBaseCliente + 35);
  document.documentElement.style.setProperty('--corBaseClienteLight', corBaseCliente + 15);

  const [lstMenusPrincipais, setLstMenusPrincipais] = useState([]);
  const [disableTooltip, setDisableTooltip] = useState(false);
  const [nomeDepartamento, setNomeDepartamento] = useState('');
  const [loadingFavoritoId, setLoadingFavoritoId] = useState(null);

  const handleFavorito = async (menuId, subMenuId, favorito) => {
    if (loadingFavoritoId === subMenuId) return;

    setLoadingFavoritoId(`${menuId}_${subMenuId}`);

    try {
      if (favorito) {
        await deletarTelaFavoritada(subMenuId);
      } else {
        await criarFavorito(subMenuId);
      }
      buscaLstMenusPrincipais(departamentoSelecionado, false);
      setLstMenusPrincipais(prev =>
        prev.map(menu =>
          menu.id === menuId
            ? {
                ...menu,
                subMenus: menu.subMenus.map(sub =>
                  sub.id === subMenuId ? { ...sub, favorito: !sub.favorito } : sub
                )
              }
            : menu
        )
      );
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingFavoritoId(null);
    }
  };

  const {
    controleRecarregarTela,
    setControleRecarregarTela,
    isPendingMenuPrincipal,
    setIsPendingMenuPrincipal,
    temClienteSelecionado,
    scrIframe,
    contextoEsteira,
    temAlteracoesPendentesEsteira,
    enviarMensagemEsteira
  } = useMenuSidebar();
  const { navegarTela, getSetMenusPrincipais } = useNavegacao({
    setEscopoAcesso
  });

  const verificaBuscaMenus = departamento => {
    if (!departamento) return;

    const lstMenusSession = getMenusDepartamento(departamento);

    if (!lstMenusSession || lstMenusSession.length <= 0) {
      buscaLstMenusPrincipais(departamento);
      return;
    }

    setLstMenusPrincipais(lstMenusSession);

    if (controleRecarregarTela) return;

    const { menuAtual, projetoAtual, rotaAtual } = buscarTelaAtual();

    const podeNavegar =
      projetoAtual && parseInt(projetoAtual) !== ProjetosEnum.PLATAFORMA_CRM && menuAtual && rotaAtual;

    setControleRecarregarTela(true);
    if (podeNavegar) {
      navegarTela(menuAtual, rotaAtual, projetoAtual);
    }
  };

  const buscaLstMenusPrincipais = (codDepartamento, loading = true) => {
    getSetMenusPrincipais({
      codDepartamento,
      fnSetData: setLstMenusPrincipais,
      fnLoading: setIsPendingMenuPrincipal,
      loading
    });
  };

  useEffect(() => {
    setDisableTooltip(false);
  }, [lstMenusPrincipais]);

  useEffect(() => {
    verificaBuscaMenus(departamentoSelecionado);
    setaNomeDepartamento(departamentoSelecionado);
  }, [departamentoSelecionado]);

  useEffect(() => {
    verificaDepartamentoSessionStorage();
  }, []);

  const verificaDepartamentoSessionStorage = () => {
    const codDepartamento = getCodDepartamentoSession();
    if (codDepartamento) {
      verificaBuscaMenus(codDepartamento);
    }
  };

  const setaNomeDepartamento = departamentoSelecionado => {
    if (departamentoSelecionado) {
      const nomeDepartamento = getNomeDepartamentoSession();
      setNomeDepartamento(nomeDepartamento);
    }
  };

  const podeExibirMenuTelaControlada = menuControlado => {
    if (menuControlado === 'S' && !temClienteSelecionado) {
      return false;
    }
    return true;
  };

  const { menuAtual, rotaAtual } = buscarTelaAtual();

  const rotaEhEsteira = useMemo(() => {
    if (typeof rotaAtual !== 'string') {
      return false;
    }

    return (
      rotaAtual.includes('credito/mesa') ||
      rotaAtual.includes('credito/auditoria') ||
      rotaAtual.includes('credito/performance')
    );
  }, [rotaAtual]);

  const iframeEhEsteira = useMemo(() => {
    if (typeof scrIframe !== 'string') {
      return false;
    }

    const iframeNormalizado = normalizarTexto(scrIframe);

    return (
      iframeNormalizado.includes('credito/mesa') ||
      iframeNormalizado.includes('credito/auditoria') ||
      iframeNormalizado.includes('credito/performance')
    );
  }, [scrIframe]);

  const possuiPropostaEmContextoAtivo =
    Boolean(contextoEsteira?.codPpc) && (rotaEhEsteira || iframeEhEsteira);

  const navegarParaEtapaEsteira = etapa => {
    if (!etapa || !contextoEsteira?.codPpc) {
      return;
    }

    const type = temAlteracoesPendentesEsteira ? 'ESTEIRA_CONFIRMAR_DESCARTE' : 'ESTEIRA_IR_PARA_ETAPA';

    enviarMensagemEsteira({
      type,
      etapa,
      codPpc: contextoEsteira.codPpc
    });
  };

  const ehMenuDaEsteira = menu => {
    const textoMenu = normalizarTexto(menu?.texto);
    const ehPorNome = textoMenu.includes('mesa de credito 2.0');
    const ehPorRota = (menu?.subMenus ?? []).some(subMenu =>
      normalizarTexto(subMenu?.rota).includes('credito-esteira')
    );

    return ehPorNome || ehPorRota;
  };

  const menuEhEsteiraCredito = menu => {
    if (!ehMenuDaEsteira(menu) || !possuiPropostaEmContextoAtivo || !menuAtual) {
      return false;
    }

    return String(menu?.id) === String(menuAtual);
  };

  const menuVisivel = menu => {
    if (!menu.subMenus?.length) return false;
    return menu.subMenus.some(subMenu => podeExibirMenuTelaControlada(subMenu.controlado));
  };

  const subMenusVisiveis = menu => {
    const subMenusFiltrados = (menu.subMenus ?? []).filter(subMenu =>
      podeExibirMenuTelaControlada(subMenu.controlado)
    );

    if (ehMenuDaEsteira(menu) && !possuiPropostaEmContextoAtivo) {
      return subMenusFiltrados.slice(0, 1);
    }

    return subMenusFiltrados;
  };

  const obterItensEsteira = menu =>
    montarItensEsteira({
      contextoEsteira: possuiPropostaEmContextoAtivo ? contextoEsteira : null,
      rotaAtual,
      subMenus: subMenusVisiveis(menu)
    });

  return {
    config,
    menuVisivel,
    disableTooltip,
    handleFavorito,
    nomeDepartamento,
    loadingFavoritoId,
    lstMenusPrincipais,
    isPendingMenuPrincipal,
    ehMenuDaEsteira,
    podeExibirMenuTelaControlada,
    menuEhEsteiraCredito,
    subMenusVisiveis,
    obterItensEsteira,
    navegarParaEtapaEsteira
  };
};

export default useListItemsHook;
